import { useEffect, useMemo, useRef, useState } from "react";
import hljs from "highlight.js/lib/core";
import json from "highlight.js/lib/languages/json";
import "highlight.js/styles/github-dark.css";

hljs.registerLanguage("json", json);

interface Props {
    /** Pretty-printed JSON string to display, or null to hide the modal. */
    value: string | null;
    onClose: () => void;
    title?: string;
}

/** Escape a string for safe insertion into innerHTML. */
const escapeHtml = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Pretty, syntax-highlighted, searchable JSON viewer modal.
 * - highlight.js colourises keys/strings/numbers.
 * - Search box marks every match (key or value) and shows a match count; Enter / Shift+Enter
 *   cycle through matches, scrolling each into view.
 * - Copy, Escape-to-close, click-outside-to-close.
 */
export const JsonViewerModal: React.FC<Props> = ({ value, onClose, title = "Raw store response" }) => {
    const [query, setQuery] = useState("");
    const [activeMatch, setActiveMatch] = useState(0);
    const preRef = useRef<HTMLPreElement>(null);

    // Syntax-highlight once per value.
    const highlighted = useMemo(() => {
        if (value == null) return "";
        try {
            return hljs.highlight(value, { language: "json" }).value;
        } catch {
            return escapeHtml(value);
        }
    }, [value]);

    // Overlay <mark> tags onto the highlighted HTML for the search query. We tag the raw text match
    // count off the plain value (reliable), and wrap matches in the highlighted HTML by operating on
    // text nodes after render (simpler + avoids breaking hljs spans): we re-highlight then mark.
    const { html, matchCount } = useMemo(() => {
        if (value == null) return { html: "", matchCount: 0 };
        if (!query.trim()) return { html: highlighted, matchCount: 0 };
        const count = (value.match(new RegExp(escapeRegExp(query), "gi")) || []).length;
        // Mark on the ESCAPED plain text (keeps it correct), losing hljs colour while searching —
        // acceptable trade-off for reliable, non-broken highlighting of matches.
        const re = new RegExp(escapeRegExp(query), "gi");
        let i = 0;
        const marked = escapeHtml(value).replace(new RegExp(escapeRegExp(escapeHtml(query)), "gi"), (m) => {
            const cls = i === activeMatch ? "bg-yellow-400 text-black" : "bg-yellow-700 text-white";
            const out = `<mark data-m="${i}" class="${cls} rounded px-0.5">${m}</mark>`;
            i++;
            return out;
        });
        void re;
        return { html: marked, matchCount: count };
    }, [value, query, highlighted, activeMatch]);

    // Reset active match when the query changes.
    useEffect(() => setActiveMatch(0), [query]);

    // Scroll the active match into view.
    useEffect(() => {
        if (!query.trim() || !preRef.current) return;
        const el = preRef.current.querySelector(`mark[data-m="${activeMatch}"]`);
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, [activeMatch, html, query]);

    // Escape closes.
    useEffect(() => {
        if (value === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [value, onClose]);

    if (value === null) return null;

    const cycle = (dir: 1 | -1) => {
        if (matchCount === 0) return;
        setActiveMatch((m) => (m + dir + matchCount) % matchCount);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                    <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
                    <div className="flex flex-1 items-center justify-end gap-2">
                        <div className="relative">
                            <input
                                type="text"
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        cycle(e.shiftKey ? -1 : 1);
                                    }
                                }}
                                placeholder="Search keys or values…"
                                className="w-56 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            {query.trim() && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                                    {matchCount === 0 ? "0" : `${activeMatch + 1}/${matchCount}`}
                                </span>
                            )}
                        </div>
                        {query.trim() && matchCount > 0 && (
                            <div className="flex items-center gap-1">
                                <button type="button" onClick={() => cycle(-1)} className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100" title="Previous (Shift+Enter)">↑</button>
                                <button type="button" onClick={() => cycle(1)} className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100" title="Next (Enter)">↓</button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => navigator.clipboard?.writeText(value)}
                            className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                            Copy
                        </button>
                        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <pre
                    ref={preRef}
                    className="hljs overflow-auto rounded-b-lg p-4 text-[12px] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            </div>
        </div>
    );
};

export default JsonViewerModal;
